
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { CreateFarmPriceInput } from '@/types/database/farm-prices';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

import FarmPriceUploadTemplate from './upload/FarmPriceUploadTemplate';
import FarmPriceFileUpload from './upload/FarmPriceFileUpload';
import FarmPriceUploadStatus from './upload/FarmPriceUploadStatus';
import FarmPricePreviewTable from './upload/FarmPricePreviewTable';
import { useFarmPriceExcelParser } from './upload/useFarmPriceExcelParser';
import { useBulkUploadProgress } from './upload/useBulkUploadProgress';

interface FarmPriceUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (prices: CreateFarmPriceInput[]) => void;
}

const FarmPriceUpload: React.FC<FarmPriceUploadProps> = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CreateFarmPriceInput[]>([]);
  const { parseExcelFile, isProcessing, error, progress, setError } = useFarmPriceExcelParser();
  const { uploadWithProgress, isUploading, uploadProgress, uploadResult, resetUpload } = useBulkUploadProgress();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setPreview([]);
    resetUpload();

    try {
      const parsedPrices = await parseExcelFile(selectedFile);
      setPreview(parsedPrices);
    } catch (err) {
      // Error is already handled in the hook
      setPreview([]);
    }
  };

  const handleUpload = async () => {
    if (preview.length === 0) return;

    try {
      const result = await uploadWithProgress(preview);
      
      if (result.successful > 0) {
        // Call the parent's onUpload callback to refresh data
        onUpload(preview);
        
        // Close dialog after successful upload (with some delay to show success message)
        if (result.failed === 0) {
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setError('');
    resetUpload();
    onClose();
  };

  const getUploadButtonText = () => {
    if (isUploading) {
      return `Uploading... ${uploadProgress.current}/${uploadProgress.total}`;
    }
    if (uploadResult) {
      if (uploadResult.failed > 0) {
        return `Retry Upload (${uploadResult.failed} failed)`;
      }
      return 'Upload Complete';
    }
    return `Upload ${preview.length} Records`;
  };

  const canUpload = preview.length > 0 && !isProcessing && !isUploading;
  const showUploadProgress = isUploading || uploadResult;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Farm Prices from Excel</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <FarmPriceUploadTemplate />

          <FarmPriceFileUpload onFileChange={handleFileChange} />

          <FarmPriceUploadStatus
            file={file}
            error={error}
            isProcessing={isProcessing}
            progress={progress}
          />

          {showUploadProgress && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {isUploading ? (
                  <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                ) : uploadResult?.failed === 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {isUploading 
                      ? `Uploading records... ${uploadProgress.current}/${uploadProgress.total}`
                      : uploadResult?.failed === 0
                      ? 'Upload completed successfully!'
                      : 'Upload completed with errors'
                    }
                  </p>
                  {uploadResult && (
                    <p className="text-xs text-gray-500 mt-1">
                      Successful: {uploadResult.successful}, Failed: {uploadResult.failed}
                    </p>
                  )}
                </div>
              </div>
              
              {isUploading && (
                <Progress 
                  value={(uploadProgress.current / uploadProgress.total) * 100} 
                  className="w-full" 
                />
              )}

              {uploadResult && uploadResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">Upload Errors:</p>
                      {uploadResult.errors.slice(0, 3).map((error, index) => (
                        <p key={index} className="text-xs">{error}</p>
                      ))}
                      {uploadResult.errors.length > 3 && (
                        <p className="text-xs">...and {uploadResult.errors.length - 3} more errors</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <FarmPricePreviewTable preview={preview} />

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              {uploadResult?.successful > 0 ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!canUpload}
            >
              {getUploadButtonText()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FarmPriceUpload;
